import React, { useCallback, useMemo, useState } from 'react';
import {
  QUICK_ASSISTANCE_REQUESTS,
  DINE_IN_ORDER_TYPE,
  type OngoingOrderSnapshot,
} from '../types/events';
import { useVirtualCaptain } from '../context/VirtualCaptainContext';

interface CaptainAssistanceProps {
  order?: OngoingOrderSnapshot;
  compact?: boolean;
  variant?: 'inline' | 'popover';
}

const CaptainAssistance: React.FC<CaptainAssistanceProps> = ({
  order,
  compact = false,
  variant = 'inline',
}) => {
  const { submitServiceRequest, callCaptain, tableAllocated, allocatedTableNumber } = useVirtualCaptain();

  // Pre-order: no order yet but table is allocated (dine-in context)
  const isPreOrder = !order && tableAllocated;
  const isDineIn = isPreOrder || (order != null && order.orderType === DINE_IN_ORDER_TYPE);
  const displayTableNumber = order?.tableNumber ?? (isPreOrder ? allocatedTableNumber : undefined);
  const displaySpaceNoun = order?.spaceNoun ?? (isPreOrder ? 'Table' : undefined);

  const visibleRequests = useMemo(() =>
    QUICK_ASSISTANCE_REQUESTS.filter((item) => {
      // BILL_INVOICE only makes sense when there's an active order
      if (item.service === 'BILL_INVOICE' && isPreOrder) return false;
      // For takeaway/delivery, only BILL_INVOICE is relevant
      if (!isDineIn && item.service !== 'BILL_INVOICE') return false;
      return true;
    }),
    [isPreOrder, isDineIn]
  );
  const [sentServices, setSentServices] = useState<Set<string>>(new Set());
  const [pendingServices, setPendingServices] = useState<Set<string>>(new Set());
  const [captainCooldown, setCaptainCooldown] = useState(false);
  const [calling, setCalling] = useState(false);

  const isPopover = variant === 'popover' || compact;

  const handleRequest = useCallback(
    async (service: string, label: string) => {
      if (sentServices.has(service) || pendingServices.has(service)) return;
      setPendingServices((prev) => new Set(prev).add(service));
      try {
        const ok = await submitServiceRequest(service, label, order ?? undefined);
        if (ok) {
          setSentServices((prev) => new Set(prev).add(service));
          setTimeout(() => {
            setSentServices((prev) => {
              const next = new Set(prev);
              next.delete(service);
              return next;
            });
          }, 30000);
        }
      } finally {
        setPendingServices((prev) => {
          const next = new Set(prev);
          next.delete(service);
          return next;
        });
      }
    },
    [sentServices, pendingServices, submitServiceRequest, order]
  );

  const handleCallCaptain = useCallback(async () => {
    if (captainCooldown || calling) return;
    setCalling(true);
    try {
      const ok = await callCaptain(order ?? undefined);
      if (ok) {
        setCaptainCooldown(true);
        setTimeout(() => {
          setCaptainCooldown(false);
          setCalling(false);
        }, 15000);
      } else {
        setCalling(false);
      }
    } catch {
      setCalling(false);
    }
  }, [captainCooldown, calling, callCaptain, order]);

  const stopBubble = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`vc-assistance${isPopover ? ' vc-assistance--popover' : ''}`}
      onClick={stopBubble}
      onMouseDown={stopBubble}
    >
      {!isPopover && (
        <>
          <div className="vc-assistance__header">
            <i className="fa fa-bell vc-assistance__header-icon" aria-hidden />
            <h4 className="vc-assistance__title">Need assistance?</h4>
            {displayTableNumber != null && (
              <span className="vc-assistance__table">{displaySpaceNoun || 'Table'} {displayTableNumber}</span>
            )}
          </div>
          <p className="vc-assistance__subtext">
            Tap any item below — your request goes directly to staff.
          </p>
        </>
      )}

      {isPopover && displayTableNumber != null && (
        <div className="vc-assistance__table-badge">{displaySpaceNoun || 'Table'} {displayTableNumber}</div>
      )}

      <div className="vc-assistance__grid">
        {visibleRequests.map((item) => {
          const isPending = pendingServices.has(item.service);
          const isSent = sentServices.has(item.service);
          return (
            <button
              key={item.service}
              type="button"
              className={`vc-assistance__btn${isSent ? ' vc-assistance__btn--sent' : ''}${isPending ? ' vc-assistance__btn--pending' : ''}`}
              onClick={() => handleRequest(item.service, item.label)}
              disabled={isSent || isPending}
              title={item.label}
            >
              <span className="vc-assistance__btn-icon" aria-hidden>
                {isPending ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <i className={`fa ${item.icon}`} />
                )}
              </span>
              <span className="vc-assistance__btn-label">{item.label}</span>
              {isSent && <i className="fa fa-check vc-assistance__sent" aria-hidden />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={`vc-assistance__call${captainCooldown || calling ? ' vc-assistance__call--active' : ''}`}
        onClick={handleCallCaptain}
        disabled={captainCooldown || calling}
      >
        {calling ? (
          <i className="fa fa-spinner fa-spin" aria-hidden />
        ) : (
          <i className="fa fa-bell" aria-hidden />
        )}
        {captainCooldown ? 'Captain notified' : calling ? 'Calling…' : 'Call Captain'}
      </button>
    </div>
  );
};

export default CaptainAssistance;
